export type EventHandler = () => void;

/**
 * A minimal event dispatcher, keyed by event name. Base class of `Store`.
 *
 * Each handler array is replaced rather than mutated, so a dispatch
 * iterates a snapshot: a handler added during a dispatch is first called
 * on the next one, and a handler removed during a dispatch is still called
 * by it if it had not been reached yet.
 * */
export default class EventDispatcher {
    private readonly eventTypeToListeners = new Map<string, EventHandler[]>();

    /**
     * Adds a listener for the given event name. Adding the same handler
     * for the same event twice has no effect.
     * @param eventName The name of the event to listen for
     * @param handler The function to call when the event is dispatched
     * */
    addEventListener(eventName: string, handler: EventHandler) {
        const handlers = this.eventTypeToListeners.get(eventName) ?? [];
        if (handlers.includes(handler)) return;
        this.eventTypeToListeners.set(eventName, [...handlers, handler]);
    }

    /**
     * Removes a listener for the given event name
     * @param eventName The name of the event to stop listening for
     * @param handler The function to stop calling when the event is dispatched
     */
    removeEventListener(eventName: string, handler: EventHandler) {
        const handlers = this.eventTypeToListeners.get(eventName);
        if (!handlers) return;
        const remaining = handlers.filter((h) => h !== handler);
        if (remaining.length > 0) {
            this.eventTypeToListeners.set(eventName, remaining);
        } else {
            this.eventTypeToListeners.delete(eventName);
        }
    }

    /**
     * Dispatches an event with the given name
     * @param eventName The name of the event to dispatch
     * */
    dispatch(eventName: string) {
        const handlers = this.eventTypeToListeners.get(eventName);
        if (!handlers) return;
        for (const handler of handlers) handler();
    }
}
